import { useState, useEffect, useMemo, useRef, SyntheticEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useField, useFormikContext } from 'formik';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';
import { ReactGoogleAutocompleteProps, usePlacesWidget } from 'react-google-autocomplete';
import DatePicker from 'react-datepicker';
import Select, { Props as ReactSelectProps } from 'react-select';
import { Popup } from 'semantic-ui-react';
import styled from 'styled-components';

// ASSETS
import NextArrow from 'assets/NextArrow';
import PreviousArrow from 'assets/PreviousArrow';
import InfoIcon from 'assets/AgreementIcons/Information';
import currencies from 'assets/Currencies';
import MinusIcon from 'assets/MinusIcon';
import { AddPictureIcon, DeleteIcon, PlusButton } from 'assets';
import PercentIcon from 'assets/PercentIcon';

// CONSTANTS
import { PRIORITIZED_COUNTRIES } from 'constants/countries.constants';

// HOOKS
import { usePrevious } from 'hooks';
import { useAppDispatch } from 'hooks/store';

// RESOURCES

// SELECTORS
import { selectUsers } from 'services/Users/selectors';
import { selectDealershipSettings } from 'services/DealershipSettings/selectors';

// STYLES
import colors from 'styles/colors';
import {
  FieldContainer,
  StyledDatePicker,
  CustomHeaderContainer,
  StyledArrowButton,
  StyledSelect,
  Input,
  Toggle,
  StyledIcon,
  ToggleContainer,
  ToggleFieldMessage,
  ToggleFieldContainer,
  ToggleFieldMessageContainer,
  PriceInput,
  QuantityInput,
  QuantityInputContainer,
  UpdateQuantityButton,
  RateInput,
  MultiCheckboxContainer,
  DateInputFieldContainer,
  ErrorText,
  FormFieldContainer,
  ImageSelector,
  ImageSelectorContainer,
  ImageRemoveButton,
  ImagePreview,
} from './styles';
import { PhoneFieldInput } from 'components/Inputs/Inputs.styles';

// THUNKS
import { getUsers } from 'services/Users/thunks';

// UTILS
import { getTranslatedRegionsForCountry } from 'utils/region.utils';
import { getValuesFromComponents } from './utils';
import { PropsOf } from 'utils/types.utils';
import Checkbox from 'components/Checkbox';

type SimulatedOnChangeEvent = {
  target: {
    name: string;
    value: any;
  };
};

type SimulatedOnBlurEvent = {
  target: {
    name: string;
  };
};

/**
 * Generic Select component, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const SelectField = (
  props: ReactSelectProps<{ label: string; value: string | null }> & {
    name: string;
    onChange?: (event: SimulatedOnChangeEvent) => void;
    onBlur?: (event: SimulatedOnBlurEvent) => void;
    placeholder?: string;
    disabled?: boolean;
    value?: any;
    dark?: boolean;
  }
) => (
  <StyledSelect
    {...props}
    instanceId={`${props.name}-selector`}
    onChange={(option) => {
      // react-select does not provide the `InputEvent` which is required by Formik.
      // event is "mocked"
      const simulatedEventPayload = {
        target: {
          name: props.name,
          value: option.value,
        },
      };

      if (props.onChange) props.onChange(simulatedEventPayload);
    }}
    onBlur={() => {
      const simulatedEventPayload = {
        target: {
          name: props.name,
        },
      };

      if (props.onBlur) props.onBlur(simulatedEventPayload);
    }}
    options={props.options}
    value={props.options?.find((option) => option.value === props.value) ?? ''} // setting to `''` to clear input's value when options are changed.
    placeholder={props.placeholder}
    isDisabled={props.disabled}
    dark={props.dark}
  />
);

/**
 * Driver Input component, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const DriverSelector = (props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const users = useSelector(selectUsers());

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  const getOptions = (usersList) => {
    return usersList
      .map((user) => {
        if (!user.firstName || !user.lastName) {
          return null;
        }
        return { value: user.id, label: `${user.firstName} ${user.lastName}` };
      })
      .filter((option) => option !== null);
  };

  return (
    <SelectField
      {...props}
      options={getOptions(users)}
      placeholder={
        props.placeholder ??
        intl.formatMessage({
          id: 'views.Valet.creationModal.noDriverPlaceholder',
        })
      }
      isDisabled={props.disabled}
    />
  );
};

/**
 * Notification Preference Selector, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const NotificationPrefSelector = (props) => {
  const intl = useIntl();

  const notificationOptions = useMemo(
    () => [
      { value: 'sms', label: intl.formatMessage({ id: 'notificationPreferences.sms' }) },
      { value: 'email', label: intl.formatMessage({ id: 'notificationPreferences.email' }) },
    ],
    [intl.locale]
  );

  return (
    <SelectField
      {...props}
      options={notificationOptions}
      placeholder={
        props.placeholder ??
        intl.formatMessage({
          id: 'services.Customers.notificationPreference',
        })
      }
      isDisabled={props.disabled}
    />
  );
};

/**
 * Language Selector, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const LanguageSelector = (props) => {
  const intl = useIntl();

  const languageOptions = useMemo(
    () => [
      { value: 'en', label: intl.formatMessage({ id: 'languages.english' }) },
      { value: 'fr', label: intl.formatMessage({ id: 'languages.french' }) },
    ],
    [intl.locale]
  );

  return (
    <SelectField
      {...props}
      options={languageOptions}
      placeholder={
        props.placeholder ??
        intl.formatMessage({
          id: 'form.placeholders.selectLanguage',
        })
      }
      isDisabled={props.disabled}
    />
  );
};

/**
 * Country Selector, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const CountrySelector = (props) => {
  const intl = useIntl();

  const options = useMemo(
    () =>
      PRIORITIZED_COUNTRIES[intl.locale.slice(0, 2).toUpperCase()].RAW.map(({ code, name }) => ({
        value: code,
        label: name,
      })),
    [intl.locale]
  );

  return (
    <SelectField
      {...props}
      options={options}
      placeholder={props.placeholder ?? intl.formatMessage({ id: 'services.Customers.selectCountry' })}
      isDisabled={props.disabled}
      dark={props.dark}
    />
  );
};

/**
 * Country Region Selector, `country` prop is required, provide an ISO 3166-1 Alpha-2 country code. Ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const RegionSelector = (props) => {
  const intl = useIntl();
  const previousCountry = usePrevious(props.country);

  const options = useMemo(() => {
    // When country or locale change, get new options.
    if (props.country) {
      return getTranslatedRegionsForCountry(props.country, intl).map(({ code, name }) => ({
        value: code,
        label: name,
      }));
    }
    return [];
  }, [props.country, intl]);

  useEffect(() => {
    if (
      // eslint-disable-next-line eqeqeq
      previousCountry &&
      props.country !== previousCountry &&
      typeof props.value === 'string' &&
      props.value.length > 0
    ) {
      // if a value is selected and country changes, wipe value.
      props.onChange({
        target: {
          name: props.name,
          value: '',
        },
      });
    }
  }, [options]);

  return (
    <SelectField
      {...props}
      options={options}
      placeholder={props.placeholder ?? intl.formatMessage({ id: 'services.Customers.stateStatic' })}
      isDisabled={props.disabled}
      dark={props.dark}
    />
  );
};

/**
 * Phone Input Component, ready to be used within Formik Field's "as" prop. Can be used on its own as well.
 */
export const PhoneField = (props: React.ComponentPropsWithoutRef<typeof PhoneFieldInput>) => {
  const settings = useSelector(selectDealershipSettings());
  const [localValue, setLocalValue] = useState(props.value);

  const handleChange = (phone) => {
    const phoneNumber = parsePhoneNumber(phone);

    if (phoneNumber) {
      const simulatedEventPayload = {
        target: {
          name: props.name,
          value: phoneNumber && phoneNumber.number,
        },
      };

      props.onChange(simulatedEventPayload);
    }

    setLocalValue(phone);
  };

  const getPhoneNumberValue = () => {
    // if formik phone number is valid or if formik is cleared, use formik value
    if (
      (props.value && isValidPhoneNumber(props.value)) ||
      props.value === '' ||
      (isValidPhoneNumber(localValue) && props.value !== String(parsePhoneNumber(localValue)?.number))
    ) {
      return props.value;
    }
    return localValue;
  };

  return (
    <FieldContainer>
      <PhoneFieldInput
        defaultCountry={settings.get('country').toLowerCase()}
        {...props}
        inputProps={{
          name: props.name,
          id: props.id,
        }}
        value={getPhoneNumberValue()}
        onChange={handleChange}
        forceDialCode
      />
    </FieldContainer>
  );
};

export const DatePickerField = (
  props: React.ComponentPropsWithoutRef<typeof DatePicker> & {
    readonly?: boolean; // TODO: Remove all references using readonly.
    errored?: boolean;
    onChange: (event: React.SyntheticEvent) => void;
  }
) => {
  const {
    dateFormat,
    errored,
    onChange,
    readOnly,
    readonly, // TODO: Remove all references using readonly.
    value,
    showTimeSelect = true,
    showTimeInput = false,
    ...rest
  } = props;

  // show only one type of time selector at a time, if TimeInput is requested, the Time Select will be invalidated;
  const shouldShowTimeSelect = showTimeInput ? false : showTimeSelect;

  const { locale } = useIntl();

  const handleChange = (date: Date) => {
    const simulatedEventPayload = {
      target: {
        name: props.name,
        value: date.toISOString(),
      },
    } as unknown as SyntheticEvent; // HACK: make type system believe this is an Event

    onChange(simulatedEventPayload);
  };

  // This disables the keyboard on mobile devices
  useEffect(() => {
    const datePickerInputs = Array.from(document.getElementsByClassName('react-datepicker__input-container'));

    for (const datePickerInput of datePickerInputs) {
      datePickerInput.getElementsByTagName('input')[0].setAttribute('readonly', '');
    }
  }, []);

  const formats = {
    'fr-CA': 'eee d MMMM - H:mm',
    'en-CA': 'eee. MMMM d - h:mm aa',
    'en-US': 'eee. MMMM d - h:mm aa',
  };

  return (
    <StyledDatePicker errored={errored} readOnly={(readOnly || readonly) ?? false}>
      <DatePicker
        {...rest}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <CustomHeaderContainer>
            <StyledArrowButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
              <PreviousArrow color={colors.black} />
            </StyledArrowButton>
            <span className="react-datepicker__current-month">
              {date.toLocaleString(locale, {
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <StyledArrowButton onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
              <NextArrow color={colors.black} />
            </StyledArrowButton>
          </CustomHeaderContainer>
        )}
        selected={value ? new Date(value) : undefined}
        onChange={handleChange}
        dateFormat={dateFormat || formats[locale]}
        readOnly={readOnly || readonly}
        locale={locale}
        showTimeSelect={shouldShowTimeSelect}
        showTimeInput={showTimeInput}
        disabledKeyboardNavigation
      />
    </StyledDatePicker>
  );
};

type GooglePlacesInputFieldProps = React.ComponentPropsWithoutRef<'input'> & { errored?: boolean } & {
  biasOrigin: {
    latitude: number;
    longitude: number;
  };
  onPlaceSelected: ReactGoogleAutocompleteProps['onPlaceSelected'];
  dark?: boolean;
};

export const GooglePlacesInputField = ({
  name,
  biasOrigin,
  errored,
  onPlaceSelected,
  value,
  ...props
}: GooglePlacesInputFieldProps) => {
  const fieldId = 'google-search-address-field';
  const { locale } = useIntl();
  const { ref, autocompleteRef } = usePlacesWidget<HTMLInputElement>({
    onPlaceSelected,
    inputAutocompleteValue: fieldId,
    language: locale,
    options: {
      types: ['address'],
      bounds: {
        north: biasOrigin.latitude + 0.1,
        south: biasOrigin.latitude - 0.1,
        east: biasOrigin.longitude + 0.1,
        west: biasOrigin.longitude - 0.1,
      },
    },
  });

  return <Input {...props} ref={ref} id={fieldId} name={name} value={value} errored={errored} />;
};

/** Wrapper around `GooglePlacesInputField` to ensure change in form values are
 *  updated in the `usePlacesWidget` further down. This is because
 * `usePlacesWidget`, does not support changes to the onPlaceSelected prop
 * once rendered. We have to force a re-render.
 * https://github.com/ErrorPro/react-google-autocomplete/issues/168 */
export const GooglePlacesInput = ({
  mapValues,
  initialValue,
  fieldValue = ({ address1, city, state, postalCode, country }) =>
    `${address1} ${city} ${state} ${postalCode} ${country}`,
  name,
  value,
  ...props
}: Omit<GooglePlacesInputFieldProps, 'onPlaceSelected'> & {
  mapValues: (...args: (string | number)[]) => { [new_name: string]: string | number };
  initialValue: string;
  fieldValue?: (addressObject: {
    address1: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  }) => string;
  name?: string;
  value?: string;
}) => {
  const onPlaceSelected = useRef((place) => undefined);
  const { values, setValues, setFieldValue } = useFormikContext();

  useEffect(() => {
    onPlaceSelected.current = async (place) => {
      if (Array.isArray(place.address_components)) {
        const { address1, city, state, postalCode, country } = getValuesFromComponents(place.address_components);

        const addressValue = fieldValue({ address1, city, state, postalCode, country });
        await setValues(
          {
            // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
            ...values,
            ...mapValues(addressValue, city, state, postalCode, country),
          },
          true
        );
      }
    };
  }, [setValues, values, mapValues, fieldValue]);

  return (
    <GooglePlacesInputField
      {...props}
      name={name}
      value={value}
      onChange={(e) => setFieldValue(name, e.target.value)}
      onPlaceSelected={(place) => onPlaceSelected.current(place)}
    />
  );
};

export const ToggleInput = ({
  inlineLabel,
  description,
  tooltipActive,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & {
  name: string;
  inlineLabel: string;
  description?: string;
  tooltipActive?: boolean;
}) => {
  return (
    <ToggleFieldContainer>
      <ToggleFieldMessageContainer>
        <ToggleFieldMessage>{inlineLabel}</ToggleFieldMessage>
        {tooltipActive && (
          <StyledIcon>
            <Popup trigger={<InfoIcon color={colors.blue} />} content={description} position="top center" />
          </StyledIcon>
        )}
      </ToggleFieldMessageContainer>
      <ToggleContainer>
        <Toggle {...props} />
      </ToggleContainer>
    </ToggleFieldContainer>
  );
};

const PriceStyling = styled.div`
  position: relative;
  > svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 4px;
  }
`;

export const PriceInputField = ({
  name,
  onBlur,
  ensureNegative = false,
  ensurePositive = false,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & {
  name: string;
  ensureNegative?: boolean;
  ensurePositive?: boolean;
}) => {
  const [input, meta, helper] = useField(name);

  const dealershipSettings = useSelector(selectDealershipSettings());
  const currency = dealershipSettings.get('currency');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = Number(event.currentTarget.value);
    let newValue = currentValue;

    // This is so that the decimal values persist when using the step functionality
    if (event.key === 'ArrowUp') {
      newValue += 1;
      event.preventDefault();
    } else if (event.key === 'ArrowDown') {
      newValue -= 1;
      event.preventDefault();
    }

    if (newValue !== currentValue) {
      helper.setValue(newValue);
    }
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    e.persist();

    if (!input.value) {
      await helper.setValue(0);
    }

    if (ensureNegative && input.value > 0) {
      await helper.setValue(-input.value);
    }

    if (ensurePositive && input.value < 0) {
      await helper.setValue(-input.value);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  const Currency = currencies[currency];

  return (
    <PriceStyling>
      <Currency />
      <PriceInput name={name} type="number" onKeyDown={handleKeyDown} onBlur={handleBlur} {...props} />
    </PriceStyling>
  );
};

const RateStyling = styled.div`
  max-width: 100%;
  position: relative;
  > svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 4px;
  }
`;

export const RateInputField = ({
  name,
  onBlur,
  ensureNegative = false,
  ensurePositive = false,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & {
  name: string;
  ensureNegative?: boolean;
  ensurePositive?: boolean;
}) => {
  const [input, meta, helper] = useField(name);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = Number(event.currentTarget.value);
    let newValue = currentValue;

    // This is so that the decimal values persist when using the step functionality
    if (event.key === 'ArrowUp') {
      newValue += 1;
      event.preventDefault();
    } else if (event.key === 'ArrowDown') {
      newValue -= 1;
      event.preventDefault();
    }

    if (newValue !== currentValue) {
      helper.setValue(newValue);
    }
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    e.persist();

    if (!input.value) {
      await helper.setValue(0);
    }

    if (ensureNegative && input.value > 0) {
      await helper.setValue(-input.value);
    }

    if (ensurePositive && input.value < 0) {
      await helper.setValue(-input.value);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <RateStyling>
      <PercentIcon />
      <RateInput name={name} type="number" onKeyDown={handleKeyDown} onBlur={handleBlur} {...props} />
    </RateStyling>
  );
};

export const QuantityInputField = ({
  name,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & {
  name: string;
}) => {
  const [input, meta, helper] = useField(name);

  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Function to validate and format the input
  const validateAndFormatInput = (value) => {
    // Remove all non-digit and non-decimal characters
    let cleanValue = value.replace(/[^\d.]/g, '');

    // Split into parts before and after decimal
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Limit integer part to 5 digits and decimal part to 2 digit
    if (integerPart.length > 5) {
      cleanValue = integerPart.slice(0, 5) + (decimalPart ? '.' + decimalPart : '');
    }
    if (decimalPart && decimalPart.length > 2) {
      cleanValue = integerPart + '.' + decimalPart.slice(0, 2);
    }

    return cleanValue;
  };

  const increaseValue = async () => {
    const currentValue = Number(input.value);
    let newValue = currentValue + 1;
    newValue = validateAndFormatInput(String(newValue));
    await helper.setValue(newValue);
  };

  const decreaseValue = async () => {
    const currentValue = Number(input.value);
    if (currentValue > 0) {
      let newValue = currentValue - 1;
      newValue = validateAndFormatInput(String(newValue));
      await helper.setValue(newValue);
    }
  };

  const handleInputChange = (event) => {
    const formattedValue = validateAndFormatInput(event.target.value);
    helper.setValue(formattedValue);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = Number(event.currentTarget.value);
    let newValue = currentValue;

    // This is so that the decimal values persist when using the step functionality
    if (event.key === 'ArrowUp') {
      newValue += 1;
      event.preventDefault();
    } else if (event.key === 'ArrowDown' && currentValue > 0) {
      newValue -= 1;
      event.preventDefault();
    }

    if (newValue !== currentValue) {
      await helper.setValue(newValue);
    }
  };

  const handleOnBlur = async () => {
    if (!input.value) {
      await helper.setValue(0);
    }
  };

  return (
    <QuantityInputContainer>
      <UpdateQuantityButton onClick={decreaseValue} icon={<MinusIcon color={colors.fleetBlue} />} />
      <QuantityInput
        name={name}
        {...props}
        onChange={handleInputChange}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        value={input.value}
      />
      <UpdateQuantityButton onClick={increaseValue} icon={<PlusButton color={colors.fleetBlue} />} />
    </QuantityInputContainer>
  );
};

export const DisableableToggle = (
  props: PropsOf<typeof ToggleInput> & { dependencyName: string; shouldUncheck: (dependencyValue: any) => boolean }
) => {
  const { dependencyName, shouldUncheck, ...rest } = props;
  const [dependencyField, dependencyMeta] = useField(dependencyName);
  const [field, _meta, helpers] = useField(props.name);

  useEffect(() => {
    if (dependencyMeta.touched && shouldUncheck(dependencyField.value) && field.value) {
      helpers.setValue(false);
    }
  }, [dependencyMeta.touched, dependencyField.value, helpers, shouldUncheck, field.value]);

  return <ToggleInput {...rest} />;
};

export const MultiSelectField = (
  props: ReactSelectProps<{ label: string; value: string | null }> & {
    name: string;
    onChange?: (event: SimulatedOnChangeEvent) => void;
    onBlur?: (event: SimulatedOnBlurEvent) => void;
    placeholder?: string;
    disabled?: boolean;
    value?: any;
  }
) => {
  return (
    <Select
      {...props}
      styles={{
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderColor: state.isFocused ? colors.fleetBlue : colors.fleetOffGray,
        }),
      }}
      instanceId={`${props.name}-selector`}
      onChange={async (options) => {
        // react-select does not provide the `InputEvent` which is required by Formik.
        // event is "mocked"
        const simulatedEventPayload = {
          target: {
            name: props.name,
            value: Array.isArray(options) ? options.map((option) => option.value) : [],
          },
        };

        if (props.onChange) {
          await props.onChange(simulatedEventPayload);
        }
        if (props.onBlur) props.onBlur(simulatedEventPayload); // Ensure "touch" is detected on selection.
      }}
      onBlur={() => {
        const simulatedEventPayload = {
          target: {
            name: props.name,
          },
        };

        if (props.onBlur) props.onBlur(simulatedEventPayload);
      }}
      value={props.options?.filter((option) => props.value?.includes(option.value))}
      isMulti
    />
  );
};

type MultiCheckboxFieldOptionValue = string | number;

type MultiCheckboxFieldOption<T extends MultiCheckboxFieldOptionValue> = {
  label: string;
  value: T;
};

interface MultiCheckboxFieldProps<T extends MultiCheckboxFieldOptionValue>
  extends React.ComponentPropsWithoutRef<'input'> {
  options: MultiCheckboxFieldOption<T>[];
  handleChange?: (value: T[]) => void;
}

export const MultiCheckboxField = <T extends MultiCheckboxFieldOptionValue>({
  name,
  options,
  handleChange,
}: MultiCheckboxFieldProps<T>) => {
  // Use Formik's useField hook to connect to field state and helpers.
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;

  // Handler to add or remove a value from the array.
  const handleCheckboxChange = (optionValue: T) => {
    // Ensure that the field value is an array.
    const currentValue = Array.isArray(field.value) ? field.value : [];
    let newValue: typeof currentValue;
    if (currentValue.includes(optionValue)) {
      // Remove the option if it's already selected.
      newValue = currentValue.filter((v) => v !== optionValue);
    } else {
      // Add the option to the array.
      newValue = [...currentValue, optionValue];
    }
    setValue(newValue);

    if (handleChange) {
      handleChange(newValue);
    }
  };

  return (
    <MultiCheckboxContainer>
      {options.map((option) => (
        <Checkbox
          key={option.value}
          onClick={() => handleCheckboxChange(option.value)}
          type="checkbox"
          checked={field.value.includes(option.value)}
          label={option.label}
          uppercase={false}
        />
      ))}
    </MultiCheckboxContainer>
  );
};

export const DateInputField = ({
  name,
  dark,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & { name: string; dark?: boolean }) => {
  const { formatMessage } = useIntl();

  const [field, meta, helpers] = useField<string>(name);
  const { value } = field;
  const { error, touched } = meta;
  const { setValue, setTouched, setError } = helpers;

  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');

  const [yearError, setYearError] = useState('');
  const [monthError, setMonthError] = useState('');
  const [dayError, setDayError] = useState('');

  useEffect(() => {
    if (value) {
      const [y = '', m = '', d = ''] = value.split('-');
      setYear(y);
      setMonth(m);
      setDay(d);
    }
  }, [value]);

  const isValidDate = (y: string, m: string, d: string) => {
    const yy = parseInt(y, 10);
    const mm = parseInt(m, 10);
    const dd = parseInt(d, 10);
    const date = new Date(yy, mm - 1, dd);

    return date.getUTCFullYear() === yy && date.getUTCMonth() + 1 === mm && date.getUTCDate() === dd;
  };

  const validateAndSync = async () => {
    let hasError = false;

    if (year.length !== 4 || isNaN(Number(year))) {
      await setYearError('errors.validation.year4Digits');
      hasError = true;
    } else {
      setYearError('');
    }

    if (month.length !== 2 || Number(month) < 1 || Number(month) > 12) {
      await setMonthError('errors.validation.invalidMonth');
      hasError = true;
    } else {
      setMonthError('');
    }

    if (day.length !== 2 || Number(day) < 1 || Number(day) > 31) {
      await setDayError('errors.validation.invalidDay');
      hasError = true;
    } else {
      setDayError('');
    }

    if (!hasError && isValidDate(year, month, day)) {
      await setValue(`${year}-${month}-${day}`);
      setTouched(true);
    } else {
      setError(formatMessage({ id: 'errors.validation.invalidDate' }));
      setTouched(true, false);
    }
  };

  const handleBlur = () => {
    validateAndSync();
  };

  const erroredDate = Boolean(yearError) || (Boolean(error) && !monthError && !dayError);

  return (
    <DateInputFieldContainer>
      <FormFieldContainer>
        <Input
          {...props}
          placeholder={formatMessage({ id: 'yyyy' })}
          value={year}
          onBlur={handleBlur}
          onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
          dark={dark}
          inputMode="decimal" // Show numeric keyboard
          errored={touched && (Boolean(yearError) || erroredDate)}
        />
        {yearError && (
          <ErrorText>
            <FormattedMessage id={yearError} />
          </ErrorText>
        )}
      </FormFieldContainer>
      <FormFieldContainer>
        <Input
          {...props}
          placeholder={formatMessage({ id: 'mm' })}
          value={month}
          onBlur={handleBlur}
          onChange={(e) => setMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
          dark={dark}
          inputMode="decimal" // Show numeric keyboard
          errored={touched && (Boolean(monthError) || erroredDate)}
        />
        {monthError && (
          <ErrorText>
            <FormattedMessage id={monthError} />
          </ErrorText>
        )}
      </FormFieldContainer>
      <FormFieldContainer>
        <Input
          {...props}
          placeholder={formatMessage({ id: 'dd' })}
          value={day}
          onBlur={handleBlur}
          onChange={(e) => setDay(e.target.value.replace(/\D/g, '').slice(0, 2))}
          dark={dark}
          inputMode="decimal" // Show numeric keyboard
          errored={touched && (Boolean(dayError) || erroredDate)}
        />
        {dayError && (
          <ErrorText>
            <FormattedMessage id={dayError} />
          </ErrorText>
        )}
      </FormFieldContainer>
    </DateInputFieldContainer>
  );
};

export const PictureSelectInput = ({ ...props }: PropsOf<typeof Input>) => {
  const [input, meta, helper] = useField(props.name);
  const { formatMessage } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const selectorRef = useRef<HTMLButtonElement>(null);
  const { className } = props;

  useEffect(() => {
    const inputReference = fileInputRef.current;

    const listener = async () => {
      const file = fileInputRef.current?.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.readyState === 2) {
            imgRef.current?.setAttribute('src', reader.result as string);
          }
        };
        reader.readAsDataURL(file);
        await helper.setValue(file);
        await helper.setTouched(true);
      }
    };
    inputReference?.addEventListener('change', listener);

    return () => {
      inputReference?.removeEventListener('change', listener);
    };
  }, [fileInputRef, helper]);

  const handleRemovePhoto = async () => {
    const fileInput = fileInputRef.current;
    if (!fileInput) return;
    fileInput.value = '';
    await helper.setValue('');
    await helper.setTouched(true);
  };

  const errored = Boolean(meta.error) && meta.touched;

  return (
    <ImageSelectorContainer>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif" style={{ display: 'none' }} />
      {input.value ? (
        <>
          <ImagePreview ref={imgRef} alt="Selected" />
          <ImageRemoveButton icon={<DeleteIcon color={colors.red} />} onClick={handleRemovePhoto}>
            {formatMessage({ id: 'buttons.delete' })}
          </ImageRemoveButton>
        </>
      ) : (
        <ImageSelector
          ref={selectorRef}
          className={className}
          onClick={(e) => {
            e.preventDefault();
            fileInputRef.current?.click();
          }}
          errored={errored}
        >
          <AddPictureIcon />
          {formatMessage({ id: 'buttons.addPhoto' })}
        </ImageSelector>
      )}
    </ImageSelectorContainer>
  );
};
